/**
 * Timesheet status enum + editability rule. Shared by the API service
 * (state-machine.ts), the API repository (status-filtered queries), the
 * typed client (`@clockwise/clients/timesheets`), and the web UI
 * (badges + edit gating).
 *
 * One file = one source of truth so the editable set can never drift
 * between server (the backstop) and client (the gate). The CHECK
 * constraint in `apps/api/sql/timesheets.sql` mirrors the same values.
 */

export const TIMESHEET_STATUSES = ['draft', 'submitted', 'approved', 'rejected', 'failed'] as const;
export type TimesheetStatus = (typeof TIMESHEET_STATUSES)[number];

/**
 * Statuses where the row is user-editable (PATCH / DELETE allowed,
 * `submit` legal from this set). The API enforces this via
 * `state-machine.canTransition('submit', ...)`; the UI uses
 * `isTimesheetEditable` to gate inline editors.
 *
 * `satisfies ReadonlyArray<TimesheetStatus>` is the drift guard — if
 * `TimesheetStatus` ever loses one of these values, the typecheck fails
 * here instead of leaking into a runtime mismatch.
 */
export const EDITABLE_TIMESHEET_STATUSES = [
  'draft',
  'rejected',
  'failed',
] as const satisfies ReadonlyArray<TimesheetStatus>;
export type EditableTimesheetStatus = (typeof EDITABLE_TIMESHEET_STATUSES)[number];

export function isTimesheetEditable(status: TimesheetStatus): boolean {
  return (EDITABLE_TIMESHEET_STATUSES as readonly TimesheetStatus[]).includes(status);
}

/**
 * Statuses that demand the user's attention — a human (rejected) or the
 * system (failed) bounced the entry and it needs editing + resubmission.
 * Drives the glanceable status dot/tint in the week grid and the
 * open-on-attention default in the cell popover. Kept here (next to
 * `isTimesheetEditable`) so the "what counts as attention" rule has one
 * home and a new attention status can't silently slip past the UI.
 *
 * `satisfies` is the drift guard — losing one of these from
 * `TimesheetStatus` fails the typecheck here.
 */
export const ATTENTION_TIMESHEET_STATUSES = [
  'rejected',
  'failed',
] as const satisfies ReadonlyArray<TimesheetStatus>;

export function isAttentionStatus(status: TimesheetStatus): boolean {
  return (ATTENTION_TIMESHEET_STATUSES as readonly TimesheetStatus[]).includes(status);
}

/**
 * Per-day rollup priority used by the week-grid status strip when a
 * single day has entries in multiple statuses. Lower number = higher
 * priority — the lowest-numbered status present on a day wins the
 * display.
 *
 * Using `Record<TimesheetStatus, number>` (not an array) so adding a
 * new status to `TIMESHEET_STATUSES` is a compile error here instead of
 * a silent fall-through to a runtime default. Keep numbers contiguous
 * so reordering is just a renumber.
 */
export const TIMESHEET_STATUS_PRIORITY: Record<TimesheetStatus, number> = {
  failed: 0,
  rejected: 1,
  submitted: 2,
  approved: 3,
  draft: 4,
};

/**
 * Pick the highest-priority status from a non-empty set, per
 * `TIMESHEET_STATUS_PRIORITY`. Returns `null` for an empty set so
 * callers can decide the "no entries" UI separately.
 */
export function highestPriorityStatus(
  statuses: ReadonlySet<TimesheetStatus> | ReadonlyArray<TimesheetStatus>,
): TimesheetStatus | null {
  let best: TimesheetStatus | null = null;
  for (const s of statuses) {
    if (best === null || TIMESHEET_STATUS_PRIORITY[s] < TIMESHEET_STATUS_PRIORITY[best]) {
      best = s;
    }
  }
  return best;
}
